// Shared data layer for the everyday Check List — general-purpose, resettable
// scratch checklists a teacher keeps per class (field-trip money, head counts,
// permission slips, etc.). Independent of attendance and of Emergency Mode.
//
// One doc per named checklist in the top-level `checklists` collection:
//   checklists/{id} {
//     ownerUid, schoolId, classId, title,
//     checkedStudentIds: [studentId, ...],   // small rosters → array is cheap
//     createdAt, updatedAt
//   }
// Checks toggle with arrayUnion/arrayRemove (atomic, no read needed).

import {
  collection, addDoc, doc, updateDoc, deleteDoc,
  arrayUnion, arrayRemove, serverTimestamp,
} from "firebase/firestore";
import { firestore } from "./FirebaseConfig";

export const CHECKLISTS = "checklists";

// Distinct, legible preset colors so each checklist is glanceable (same palette
// shape as the lending trackers).
export const CHECKLIST_COLORS = ["#2563eb", "#16a34a", "#dc2626", "#d97706", "#7c3aed", "#0891b2", "#db2777", "#65a30d"];

export function createChecklist({ ownerUid, schoolId, classId, title, color }) {
  return addDoc(collection(firestore, CHECKLISTS), {
    ownerUid,
    schoolId: schoolId || "",
    classId,
    title: title || "Untitled checklist",
    color: color || CHECKLIST_COLORS[0],
    checkedStudentIds: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function setChecklistColor(id, color) {
  return updateDoc(doc(firestore, CHECKLISTS, id), {
    color: color || CHECKLIST_COLORS[0],
    updatedAt: serverTimestamp(),
  });
}

export function renameChecklist(id, title) {
  return updateDoc(doc(firestore, CHECKLISTS, id), {
    title: title || "Untitled checklist",
    updatedAt: serverTimestamp(),
  });
}

export function deleteChecklist(id) {
  return deleteDoc(doc(firestore, CHECKLISTS, id));
}

// Add or remove a single student from the checklist. `on` = should be checked.
export function toggleCheck(id, studentId, on) {
  return updateDoc(doc(firestore, CHECKLISTS, id), {
    checkedStudentIds: on ? arrayUnion(studentId) : arrayRemove(studentId),
    updatedAt: serverTimestamp(),
  });
}

// Clear every check so the list can be reused for the next purpose.
export function resetChecklist(id) {
  return updateDoc(doc(firestore, CHECKLISTS, id), {
    checkedStudentIds: [],
    updatedAt: serverTimestamp(),
  });
}
